import { useEffect } from 'react'
import { useParams } from 'react-router-dom'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add } from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import SalaryModal from 'modules/job_catalog/components/SalaryModal'
import { useJobCatalogTreeVisibility } from 'modules/job_catalog/hooks/useJobCatalogTreeVisibility'
import { getValidNodeIdFromParams, normalizeNodeType } from 'modules/job_catalog/lib/nodesUtils'
import {
  formatSalaryFromCentsObject,
  getSalaryFrequencyText,
} from 'modules/job_catalog/lib/salaryUtils'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import { recordIsSalary, useSalaryPage } from './useSalaryPage'

const SalariesPage = () => {
  const { rootId, familyId, functionId, roleId, levelId } = useParams()
  const id = getValidNodeIdFromParams(levelId, roleId, functionId, familyId, rootId)
  const { canManageTree } = useJobCatalogTreeVisibility()

  const {
    source,
    nodeName,
    nodeType,
    selectedWorkplaces,
    hasAllWorkplacesSalary,
    editSalary,
    handleCreateSalary,
    handleUpdateSalary,
    showSalaryModal,
    setShowSalaryModal,
  } = useSalaryPage(id, canManageTree)

  useEffect(() => {
    if (!id) return
    track(trackingEvents.jobCatalog.navigateNodeSubmit, {
      event_category: 'Organizations',
      node_type: normalizeNodeType(id),
      node_id: id,
      tab: 'salaries',
    })
  }, [id])

  return (
    <ResponsivePage.Body variant="full">
      <OneDataCollection
        key={`salaries-${id}`}
        source={source}
        emptyStates={{
          'no-data': {
            emoji: '💰',
            title: i18n.t('job_catalog.nodes.empty_state.salaries.title'),
            description: i18n.t('job_catalog.nodes.empty_state.salaries.description'),
            actions: canManageTree
              ? [
                  {
                    label: i18n.t('job_catalog.nodes.empty_state.salaries.actions.new'),
                    onClick: () => setShowSalaryModal(true),
                    icon: Add,
                  },
                ]
              : [],
          },
        }}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('job_catalog.nodes.salaries.workplace'),
                  render: (record) => {
                    if (!recordIsSalary(record)) return
                    if (record.workplaces && record.workplaces.length > 0) {
                      return {
                        type: 'text',
                        value: record.workplaces.map((workplace) => workplace.name).join(', '),
                      }
                    } else {
                      return {
                        type: 'text',
                        value: i18n.t('job_catalog.nodes.salaries.all_workplaces'),
                      }
                    }
                  },
                },
                {
                  label: i18n.t('job_catalog.nodes.salary'),
                  render: (record) => {
                    if (!recordIsSalary(record)) return
                    return {
                      type: 'text',
                      value: formatSalaryFromCentsObject(record),
                    }
                  },
                },
                {
                  label: i18n.t('job_catalog.nodes.salaries.frequency'),
                  render: (record) => {
                    if (!recordIsSalary(record)) return
                    return {
                      type: 'text',
                      value: getSalaryFrequencyText(record.periodicity),
                    }
                  },
                },
              ],
              frozenColumns: 1,
            },
          },
        ]}
      />
      {showSalaryModal && (
        <SalaryModal
          onClose={() => setShowSalaryModal(false)}
          name={nodeName}
          createAction={handleCreateSalary}
          updateAction={handleUpdateSalary}
          onSubmit={() => setShowSalaryModal(false)}
          nodeType={nodeType}
          initialSalary={editSalary}
          alreadySelectedWorkplaces={selectedWorkplaces}
          hasAllWorkplacesSalary={hasAllWorkplacesSalary}
        />
      )}
    </ResponsivePage.Body>
  )
}
export default SalariesPage
